<template>
  <Transition appear name="s-alert">
    <div
      v-if="isVisible"
      class="s-alert"
      :class="[`s-alert--${color}`, `s-alert--size-${size}`]"
      v-bind="{ ...liveAttrs, ...$attrs }"
    >
      <i
        v-if="prefixIcon"
        class="s-alert__icon s-alert__prefix"
        :class="prefixIcon"
      />
      <div class="s-alert__content">
        <slot />
      </div>
      <i
        v-if="suffixIcon"
        class="s-alert__icon s-alert__suffix"
        :class="suffixIcon"
      />
      <SAlertClose v-if="closable" :color="color" @click="close" />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { UseAlertEmit } from "@/lib/components/logic/atoms/useAlert";

import SAlertClose from "@/lib/components/atoms/alert/SAlertClose.vue";
import * as useAlert from "@/lib/components/logic/atoms/useAlert";

// The Transition root is abstract, so attrs must be forwarded to the div by hand
defineOptions({ inheritAttrs: false });

const props = defineProps(useAlert.props);
const emit = defineEmits<UseAlertEmit>();

const {
  color,
  prefixIcon,
  suffixIcon,
  size,
  closable,
  liveAttrs,
  isVisible,
  close,
} = useAlert.use(props, emit);
</script>

<style lang="postcss">
.s-alert {
  @apply flex items-center rounded-md border gap-2;

  &--size {
    &-lg {
      @apply px-3.5 py-2.5 text-xl;

      .s-alert__icon {
        @apply h-5 w-5 text-xl;
      }
    }

    &-md {
      @apply px-3 py-2 text-base;

      .s-alert__icon {
        @apply h-[1.125rem] w-[1.125rem] text-lg;
      }
    }

    &-sm {
      @apply px-2.5 py-1.5 text-sm;

      .s-alert__icon {
        @apply h-4 w-4 text-sm;
      }
    }
  }

  &__icon {
    @apply block shrink-0;
  }

  &__content {
    @apply flex-grow font-medium;
  }

  &__close {
    @apply ml-auto shrink-0;
  }

  &--primary {
    @apply border-primary-200 bg-primary-50 text-primary-600 shadow-[0px_4px_8px_0px_theme(colors.primary.500/0.04)];

    /* The default quaternary hover is primary-50, the alert's own background */
    .s-alert__close {
      --button-quaternary-hover-bg: theme("colors.primary.100");
    }
  }

  &--accent {
    @apply border-accent-200 bg-accent-50 text-accent-600 shadow-[0px_4px_8px_0px_theme(colors.accent.500/0.04)];
  }

  &--success {
    @apply border-success-200 bg-success-50 text-success-600 shadow-[0px_4px_8px_0px_theme(colors.success.500/0.04)];
  }

  &--warning {
    @apply border-warning-200 bg-warning-50 text-warning-700 shadow-[0px_4px_8px_0px_theme(colors.warning.500/0.04)];

    /* The default quaternary warning-500 is 2.4:1 on warning-50, below the 3:1 icon minimum */
    .s-alert__close {
      --button-quaternary-text: theme("colors.warning.700");
    }
  }

  &--danger {
    @apply border-danger-200 bg-danger-50 text-danger-600 shadow-[0px_4px_8px_0px_theme(colors.danger.500/0.04)];
  }

  &--gray {
    @apply border-gray-200 bg-gray-100 text-gray-600 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.04)];
  }

  &-enter-from {
    @apply opacity-0;
  }

  &-enter-active {
    @apply transition-opacity duration-300;
  }

  &&-leave-from {
    @apply max-h-[1000px];
  }

  &&-leave-to {
    @apply m-0 max-h-0 opacity-0;
  }

  &-leave-active {
    overflow: hidden;
    transition:
      max-height 0.45s cubic-bezier(0, 1, 0, 1),
      opacity 0.3s,
      margin 0.3s;
  }
}
</style>
